import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { getApi } from './api';
import { computed, unref, watch } from 'vue';
import invalidateAiCommandResults from './invalidateAiCommandResults';

const isForbidden = (error: any) => error?.response?.status === 403;

export const FINAL_AI_COMMAND_STATUSES = ['completed', 'failed', 'reverted'];

// A refusal is final, so neither retry nor keep polling it.
export const retryAiCommand = (failureCount: number, error: any) =>
    !isForbidden(error) && failureCount < 3;

export const aiCommandRefetchInterval = (query: any) => {
    if (isForbidden(query?.state?.error)) return false;
    return FINAL_AI_COMMAND_STATUSES.includes(query?.state?.data?.status) ? false : 10000;
};

/**
 * Gets AI command information by its ID
 * @param accountId - Account ID (can be ref)
 * @param commandId - Command ID (can be ref)
 * @returns Query object with command data
 */
export default (accountId: any, commandId: any) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ['ai-command', accountId, commandId],
        queryFn: async () => {
            const { api, baseURL } = getApi();
            const actualAccountId = unref(accountId);
            const actualCommandId = unref(commandId);

            const response = await api.get(
                `/api/v2/accounts/${actualAccountId}/ai-commands/${actualCommandId}`,
                { baseURL, useOriginalToken: true, skipForbiddenRedirect: true }
            );
            return response.data;
        },
        enabled: computed(() => !!(unref(accountId) && unref(commandId))),
        staleTime: 30000, // 30 seconds
        refetchInterval: aiCommandRefetchInterval,
        retry: retryAiCommand,
    });

    watch(
        () => query.data.value?.status,
        (status, previous) => {
            if (previous && status !== previous && ['completed', 'reverted'].includes(status)) {
                invalidateAiCommandResults(queryClient);
            }
        },
    );

    return query;
};
